import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { http, API } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Tabs as ModeTabs, TabsList as ModeList, TabsTrigger as ModeTrigger, TabsContent as ModeContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { FileText, FileDown, Presentation, Printer, Loader2, History, Send, Clock, ArrowRight, Sparkles, Star, ExternalLink, Upload, Wand2, FileUp } from "lucide-react";
import Comments from "@/components/Comments";

export default function PaperAssistant() {
  const [form, setForm] = useState({ topic: "", goal: "", language: "id", target_standard: "Scopus Q2" });
  const [loading, setLoading] = useState(false);
  const [paper, setPaper] = useState(null);
  const [paperId, setPaperId] = useState(null);
  const [exporting, setExporting] = useState("");
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [recommending, setRecommending] = useState(false);
  const [recommendations, setRecommendations] = useState(null);
  const [mode, setMode] = useState("generate");
  const [uploading, setUploading] = useState(false);
  const [uploadMeta, setUploadMeta] = useState(null);
  const [template, setTemplate] = useState("standard");
  const [plagBusy, setPlagBusy] = useState(false);
  const [plagResult, setPlagResult] = useState(null);
  const fileRef = useRef(null);
  const nav = useNavigate();

  const set = (k, v) => setForm({ ...form, [k]: v });

  const loadHistory = async () => {
    try { const { data } = await http.get("/my/papers"); setHistory(data); } catch {}
  };
  useEffect(() => { loadHistory(); }, []);

  const generate = async () => {
    if (!form.topic.trim() || !form.goal.trim()) { toast.error("Isi topik dan tujuan."); return; }
    setLoading(true); setPaper(null);
    try {
      const { data: job } = await http.post("/ai/paper", form);
      // poll every 3s up to 3 min
      const jobId = job.job_id;
      const started = Date.now();
      while (Date.now() - started < 180000) {
        await new Promise(r => setTimeout(r, 3000));
        const { data: j } = await http.get(`/ai/job/${jobId}`);
        if (j.status === "done") {
          setPaper(j.result.paper); setPaperId(j.result.paper_id);
          toast.success("Paper & slide berhasil dibuat");
          loadHistory();
          return;
        }
        if (j.status === "error") throw new Error(j.error || "AI error");
      }
      throw new Error("Timeout menunggu AI");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "AI gagal menghasilkan paper");
    } finally { setLoading(false); }
  };

  const download = async (kind) => {
    if (!paper) return;
    setExporting(kind);
    try {
      const token = localStorage.getItem("kt_token");
      const res = await fetch(`${API}/export/${kind}`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ paper, template }),
      });
      if (!res.ok) throw new Error("Export gagal");
      const blob = await res.blob();
      const ext = kind === "docx" ? "docx" : kind === "pptx" ? "pptx" : "pdf";
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = `paper-${template}.${ext}`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Gagal mengunduh"); }
    finally { setExporting(""); }
  };

  const runPlagiarism = async () => {
    if (!paper) return;
    setPlagBusy(true); setPlagResult(null);
    try {
      const { data } = await http.post("/ai/plagiarism-check", { paper, paper_id: paperId });
      setPlagResult(data);
      toast.success(`Scan selesai · ${data.verdict || ""}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menjalankan scan");
    } finally { setPlagBusy(false); }
  };

  const printView = () => window.print();

  const openHistoryItem = (item) => {
    setPaper(item.data);
    setPaperId(item.paper_id);
    setForm({
      topic: item.input?.topic || "",
      goal: item.input?.goal || "",
      language: item.input?.language || "id",
      target_standard: item.input?.target_standard || "Scopus Q2",
    });
    setShowHistory(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
    toast.success("Paper dari riwayat dimuat");
  };

  const submitToJournal = () => {
    if (!paper) return;
    const target = form.target_standard || "ALL";
    nav(`/direktori-jurnal?from=paper&indexing=${encodeURIComponent(target)}&q=${encodeURIComponent(form.topic || "")}`);
  };

  const recommend = async () => {
    if (!paper) return;
    setRecommending(true); setRecommendations(null);
    try {
      const { data } = await http.post("/ai/recommend-journals", {
        paper_id: paperId,
        paper,
        target_indexing: form.target_standard || "ALL",
      });
      setRecommendations(data.recommendations || []);
      if (!(data.recommendations || []).length) toast.message("Belum ada rekomendasi yang pas, coba ubah target standar.");
      else toast.success("Rekomendasi jurnal AI siap!");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI gagal memberi rekomendasi");
    } finally { setRecommending(false); }
  };

  const uploadManuscript = async (file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error("Max 10MB"); return; }
    const okExt = /\.(pdf|docx|txt)$/i.test(file.name);
    if (!okExt) { toast.error("Format file: .pdf, .docx, atau .txt"); return; }
    setUploading(true); setPaper(null); setUploadMeta(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data: job } = await http.post(
        `/ai/paper/upload?target_standard=${encodeURIComponent(form.target_standard)}&language=${form.language}`,
        fd, { headers: { "Content-Type": "multipart/form-data" } }
      );
      toast.success(`Naskah terunggah (${(job.source_chars||0).toLocaleString("id-ID")} karakter) · AI sedang merapikan…`);
      setUploadMeta({ filename: file.name, chars: job.source_chars, kind: job.detected_kind });
      const jobId = job.job_id;
      const started = Date.now();
      while (Date.now() - started < 180000) {
        await new Promise(r => setTimeout(r, 3000));
        const { data: j } = await http.get(`/ai/job/${jobId}`);
        if (j.status === "done") {
          setPaper(j.result.paper); setPaperId(j.result.paper_id);
          setForm(f => ({ ...f, topic: j.result.paper.title || file.name, goal: "Reformat manuscript upload" }));
          loadHistory();
          toast.success("Naskah berhasil diformat ulang & siap submit!");
          return;
        }
        if (j.status === "error") throw new Error(j.error || "AI error");
      }
      throw new Error("Timeout menunggu AI");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Gagal memproses naskah");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-widest text-[#D97706] font-semibold">AI Paper & Slide</p>
          <h1 className="serif text-3xl font-bold text-[#0F2922] mt-1">Asisten AI Paper, Jurnal & Slide Presentasi</h1>
          <p className="text-slate-600 mt-2">Isi topik risetmu — dapatkan draft paper lengkap + 10 slide presentasi.</p>
        </div>
        <Button variant="outline" onClick={() => setShowHistory(s => !s)} data-testid="paper-history-toggle">
          <History size={16} className="mr-2"/> Riwayat
          {history.length > 0 && <Badge className="ml-2 bg-[#D97706] text-white border-0">{history.length}</Badge>}
        </Button>
      </div>

      {showHistory && (
        <div className="mt-5 kt-card p-5" data-testid="paper-history-panel">
          <div className="flex items-center gap-2 mb-3">
            <Clock size={16} className="text-[#D97706]"/>
            <h2 className="font-semibold text-[#0F2922]">Riwayat Paper & Slide Saya</h2>
          </div>
          {history.length === 0 && <div className="text-sm text-slate-400 italic py-6 text-center">Belum ada paper yang pernah dibuat.</div>}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {history.map(h => (
              <button
                key={h.paper_id}
                onClick={() => openHistoryItem(h)}
                data-testid={`paper-history-${h.paper_id}`}
                className="text-left rounded-xl border border-slate-200 hover:border-[#D97706] hover:shadow-md transition p-4 bg-white"
              >
                <div className="flex items-center gap-2">
                  <Badge className="bg-[#0F2922] text-[#FAFA96] text-[10px]">{h.input?.target_standard || "-"}</Badge>
                  <Badge variant="outline" className="text-[10px]">{(h.input?.language || "id").toUpperCase()}</Badge>
                </div>
                <div className="font-semibold text-[#0F2922] mt-2 text-sm line-clamp-2">{h.data?.title || h.input?.topic || "Untitled"}</div>
                <p className="text-xs text-slate-500 mt-1 line-clamp-2">{h.input?.topic}</p>
                <div className="text-[11px] text-slate-400 mt-2 flex items-center justify-between">
                  <span>{new Date(h.created_at).toLocaleString("id-ID", { dateStyle: "medium", timeStyle: "short" })}</span>
                  <span className="text-[#D97706] font-semibold inline-flex items-center gap-1">Buka <ArrowRight size={11}/></span>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="mt-6 kt-card p-0 overflow-hidden">
        <Tabs value={mode} onValueChange={setMode} className="w-full">
          <TabsList className="w-full rounded-none border-b border-slate-200 bg-[#FAFA96]/40 h-auto p-0">
            <TabsTrigger value="generate" className="flex-1 py-3 data-[state=active]:bg-white rounded-none" data-testid="mode-generate">
              <Sparkles size={14} className="mr-2"/> Generate dari Topik
            </TabsTrigger>
            <TabsTrigger value="upload" className="flex-1 py-3 data-[state=active]:bg-white rounded-none" data-testid="mode-upload">
              <FileUp size={14} className="mr-2"/> Unggah Naskah Saya
            </TabsTrigger>
          </TabsList>

          <TabsContent value="generate" className="p-6 grid md:grid-cols-2 gap-4 mt-0">
            <div className="md:col-span-2"><Label>Topik / Judul kerja</Label>
              <Input value={form.topic} onChange={e=>set("topic", e.target.value)} placeholder="Contoh: Penerapan machine learning untuk deteksi dini stunting" data-testid="paper-topic-input" />
            </div>
            <div className="md:col-span-2"><Label>Tujuan riset</Label>
              <Textarea value={form.goal} onChange={e=>set("goal", e.target.value)} placeholder="Contoh: Mengembangkan model klasifikasi berbasis data posyandu…" data-testid="paper-goal-input" />
            </div>
            <div><Label>Bahasa</Label>
              <Select value={form.language} onValueChange={v=>set("language", v)}>
                <SelectTrigger data-testid="paper-language-select"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="id">Bahasa Indonesia</SelectItem>
                  <SelectItem value="en">English</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Target standar</Label>
              <Select value={form.target_standard} onValueChange={v=>set("target_standard", v)}>
                <SelectTrigger data-testid="paper-target-select"><SelectValue/></SelectTrigger>
                <SelectContent>
                  {["Scopus Q1","Scopus Q2","Scopus Q3","Scopus Q4","Sinta 2","Sinta 3","Sinta 4"].map(x=><SelectItem key={x} value={x}>{x}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2">
              <Button disabled={loading} onClick={generate} className="bg-[#0F2922] hover:bg-[#173E34] text-[#FAFA96]" data-testid="paper-generate-btn">
                {loading ? <><Loader2 className="mr-2 animate-spin" size={16}/> Menghasilkan… (30-60 detik)</> : "Generate Paper + Slide dengan AI"}
              </Button>
            </div>
          </TabsContent>

          <TabsContent value="upload" className="p-6 mt-0">
            <div className="grid md:grid-cols-2 gap-4 mb-5">
              <div><Label>Bahasa output</Label>
                <Select value={form.language} onValueChange={v=>set("language", v)}>
                  <SelectTrigger><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="id">Bahasa Indonesia</SelectItem>
                    <SelectItem value="en">English</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Target standar jurnal</Label>
                <Select value={form.target_standard} onValueChange={v=>set("target_standard", v)}>
                  <SelectTrigger><SelectValue/></SelectTrigger>
                  <SelectContent>
                    {["Scopus Q1","Scopus Q2","Scopus Q3","Scopus Q4","Sinta 2","Sinta 3","Sinta 4"].map(x=><SelectItem key={x} value={x}>{x}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <input ref={fileRef} type="file" hidden accept=".pdf,.docx,.txt" onChange={e => uploadManuscript(e.target.files?.[0])} data-testid="paper-upload-input"/>
            <div
              onClick={() => !uploading && fileRef.current?.click()}
              onDragOver={e => { e.preventDefault(); }}
              onDrop={e => { e.preventDefault(); if (!uploading && e.dataTransfer.files?.[0]) uploadManuscript(e.dataTransfer.files[0]); }}
              className={`rounded-2xl border-2 border-dashed p-10 text-center transition cursor-pointer ${uploading ? "border-slate-200 bg-slate-50 cursor-wait" : "border-[#D97706]/40 bg-[#FAFA96]/30 hover:bg-[#FAFA96]/60 hover:border-[#D97706]"}`}
              data-testid="paper-upload-dropzone"
            >
              {uploading ? (
                <>
                  <Loader2 size={44} className="mx-auto text-[#D97706] animate-spin"/>
                  <p className="mt-3 font-semibold text-[#0F2922]">AI sedang merapikan naskah Anda…</p>
                  {uploadMeta && <p className="text-xs text-slate-500 mt-1">{uploadMeta.filename} · {uploadMeta.chars?.toLocaleString("id-ID")} karakter · format {uploadMeta.kind?.toUpperCase()}</p>}
                  <p className="text-xs text-slate-500 mt-1">Biasanya 30-60 detik — paper + 10 slide akan siap diekspor.</p>
                </>
              ) : (
                <>
                  <div className="w-14 h-14 mx-auto rounded-2xl bg-[#0F2922] text-[#FAFA96] flex items-center justify-center"><Upload size={26}/></div>
                  <p className="mt-3 font-semibold text-[#0F2922]">Seret & lepas naskah di sini, atau klik untuk pilih file</p>
                  <p className="text-sm text-slate-600 mt-1">AI akan merapikan naskah agar sesuai tata tulis <b>{form.target_standard}</b> dan siap submit ke jurnal.</p>
                  <div className="mt-3 flex justify-center gap-2 flex-wrap text-xs">
                    <Badge variant="outline">.PDF</Badge>
                    <Badge variant="outline">.DOCX</Badge>
                    <Badge variant="outline">.TXT</Badge>
                    <Badge variant="outline" className="border-[#D97706] text-[#D97706]">Maks 10 MB</Badge>
                  </div>
                  <Button type="button" className="mt-5 bg-[#D97706] hover:bg-[#B45309] text-white" data-testid="paper-upload-btn">
                    <Wand2 size={14} className="mr-2"/> Pilih file & format ulang
                  </Button>
                </>
              )}
            </div>

            <div className="mt-4 grid sm:grid-cols-3 gap-3 text-xs">
              {[
                { icon: Wand2, title: "Perbaiki Tata Tulis", desc: "Grammar, ejaan, konsistensi tense & heading sesuai standar jurnal." },
                { icon: FileText, title: "Struktur Lengkap", desc: "Abstrak, pendahuluan, tinjauan pustaka, metodologi, hasil, pembahasan, kesimpulan." },
                { icon: Presentation, title: "Bonus 10 Slide", desc: "Slide presentasi otomatis dari naskahmu, siap ekspor PPT." },
              ].map((f, i) => (
                <div key={i} className="rounded-xl border border-slate-200 p-3 bg-white">
                  <div className="flex items-center gap-2 text-[#D97706]"><f.icon size={14}/><b className="text-[#0F2922] text-sm">{f.title}</b></div>
                  <div className="text-slate-600 mt-1">{f.desc}</div>
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {paper && (
        <div className="mt-8 kt-card p-6" id="paper-print">
          <div className="flex flex-wrap gap-2 print:hidden items-center">
            <div className="flex items-center gap-2 mr-2">
              <Label className="text-xs text-slate-500 whitespace-nowrap">Template:</Label>
              <Select value={template} onValueChange={setTemplate}>
                <SelectTrigger className="w-40 h-9" data-testid="paper-template-select"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="standard">Standar (Default)</SelectItem>
                  <SelectItem value="ieee">IEEE (2 kolom)</SelectItem>
                  <SelectItem value="elsevier">Elsevier</SelectItem>
                  <SelectItem value="sinta-ui">SINTA · UI</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button variant="outline" onClick={() => download("pdf")} disabled={!!exporting} data-testid="export-pdf-btn"><FileDown size={16} className="mr-2"/>PDF</Button>
            <Button variant="outline" onClick={() => download("docx")} disabled={!!exporting} data-testid="export-word-btn"><FileText size={16} className="mr-2"/>Word</Button>
            <Button variant="outline" onClick={() => download("pptx")} disabled={!!exporting} data-testid="export-ppt-btn"><Presentation size={16} className="mr-2"/>PowerPoint</Button>
            <Button variant="outline" onClick={printView} data-testid="export-print-btn"><Printer size={16} className="mr-2"/>Cetak</Button>
            <Button onClick={submitToJournal} className="bg-[#D97706] hover:bg-[#B45309] text-white ml-auto" data-testid="submit-to-journal-btn">
              <Send size={16} className="mr-2"/> Submit ke Jurnal
            </Button>
          </div>

          <Tabs defaultValue="paper" className="mt-6">
            <TabsList>
              <TabsTrigger value="paper" data-testid="tab-paper">Paper</TabsTrigger>
              <TabsTrigger value="slides" data-testid="tab-slides">Slide Presentasi</TabsTrigger>
            </TabsList>
            <TabsContent value="paper">
              <h2 className="serif text-2xl font-bold text-[#0F2922] mt-4">{paper.title}</h2>
              <p className="text-sm text-slate-500 mt-1"><b>Keywords:</b> {(paper.keywords || []).join(", ")}</p>
              {[
                ["Abstract", paper.abstract],
                ["Introduction", paper.introduction],
                ["Literature Review", paper.literature_review],
                ["Methodology", paper.methodology],
                ["Results", paper.results],
                ["Discussion", paper.discussion],
                ["Conclusion", paper.conclusion],
              ].map(([h, body]) => (
                <div key={h} className="mt-5">
                  <h3 className="font-semibold text-[#0F2922]">{h}</h3>
                  <p className="text-sm text-slate-700 whitespace-pre-line mt-1 leading-relaxed">{body}</p>
                </div>
              ))}
              <div className="mt-5">
                <h3 className="font-semibold text-[#0F2922]">References</h3>
                <ol className="list-decimal pl-5 text-sm text-slate-700 mt-1 space-y-1">
                  {(paper.references || []).map((r,i)=><li key={i}>{r}</li>)}
                </ol>
              </div>
            </TabsContent>
            <TabsContent value="slides">
              <div className="grid md:grid-cols-2 gap-4 mt-4">
                {(paper.slides || []).map((s, i) => (
                  <div key={i} className="border border-slate-200 rounded-xl p-4 bg-[#FAFA96]/30">
                    <div className="text-xs text-[#D97706] uppercase tracking-widest font-semibold">Slide {i+1}</div>
                    <h4 className="font-semibold text-[#0F2922] mt-1">{s.title}</h4>
                    <ul className="list-disc pl-5 text-sm text-slate-700 mt-2 space-y-1">
                      {(s.bullets || []).map((b,j)=><li key={j}>{b}</li>)}
                    </ul>
                    {s.speaker_notes && <p className="text-xs text-slate-500 mt-2 italic">Speaker notes: {s.speaker_notes}</p>}
                  </div>
                ))}
              </div>
            </TabsContent>
          </Tabs>
          {paperId && <Comments contentType="paper" contentId={paperId} />}
        </div>
      )}

      {paper && (
        <div className="mt-6 rounded-2xl overflow-hidden border border-red-200 bg-gradient-to-br from-red-50 to-white" data-testid="plagiarism-panel">
          <div className="p-6 flex items-start gap-4 flex-wrap">
            <div className="w-11 h-11 rounded-xl bg-red-600 text-white flex items-center justify-center shrink-0"><FileText size={22}/></div>
            <div className="flex-1 min-w-[240px]">
              <h3 className="serif text-xl font-bold text-[#0F2922]">Plagiarism Pre-Check (AI Similarity Scan)</h3>
              <p className="text-sm text-slate-600 mt-1">Scan AI heuristik untuk mendeteksi frasa generik, klaim tanpa sitasi, dan paragraf berisiko kesamaan — <b>bukan</b> pengganti Turnitin/iThenticate, tapi berguna sebelum submit.</p>
            </div>
            <Button onClick={runPlagiarism} disabled={plagBusy} className="bg-red-600 hover:bg-red-700 text-white" data-testid="plagiarism-btn">
              {plagBusy ? <><Loader2 className="mr-2 animate-spin" size={14}/> Memindai…</> : <><FileText size={14} className="mr-2"/> Jalankan Scan</>}
            </Button>
          </div>
          {plagResult && (
            <div className="px-6 pb-6">
              <div className="rounded-xl bg-white border border-slate-200 p-4 flex flex-wrap items-center gap-4">
                <div className="relative w-20 h-20 rounded-full flex items-center justify-center font-bold text-2xl text-white shrink-0" style={{ background: `conic-gradient(${plagResult.overall_score < 25 ? "#059669" : plagResult.overall_score < 50 ? "#D97706" : "#DC2626"} ${plagResult.overall_score * 3.6}deg, #E5E7EB 0deg)` }}>
                  <div className="absolute inset-1 rounded-full bg-white flex items-center justify-center text-[#0F2922]">{Math.round(plagResult.overall_score)}%</div>
                </div>
                <div className="flex-1 min-w-[200px]">
                  <Badge className={`${plagResult.verdict === "aman" ? "bg-emerald-600" : plagResult.verdict === "perlu perbaikan" ? "bg-amber-500" : "bg-red-600"} text-white border-0 uppercase`}>{plagResult.verdict || "N/A"}</Badge>
                  <p className="text-sm text-slate-700 mt-2">{plagResult.summary}</p>
                </div>
              </div>
              {plagResult.sections?.length > 0 && (
                <div className="grid md:grid-cols-2 gap-3 mt-4">
                  {plagResult.sections.map((s, i) => (
                    <div key={i} className="rounded-xl border border-slate-200 bg-white p-4">
                      <div className="flex items-center justify-between">
                        <b className="text-[#0F2922] text-sm uppercase">{s.section}</b>
                        <span className={`text-sm font-bold ${s.score < 25 ? "text-emerald-600" : s.score < 50 ? "text-amber-600" : "text-red-600"}`}>{s.score}%</span>
                      </div>
                      {s.flagged_phrases?.length > 0 && (
                        <div className="mt-2">
                          <div className="text-[11px] uppercase text-slate-400">Frasa berisiko</div>
                          <div className="flex flex-wrap gap-1 mt-1">{s.flagged_phrases.map((p, j) => <Badge key={j} variant="outline" className="text-[10px] border-red-200 text-red-600 italic">"{p}"</Badge>)}</div>
                        </div>
                      )}
                      {s.suggestions?.length > 0 && (
                        <ul className="text-xs text-slate-700 mt-2 space-y-1 list-disc pl-4">
                          {s.suggestions.map((x, j) => <li key={j}>{x}</li>)}
                        </ul>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {plagResult.action_items?.length > 0 && (
                <div className="mt-4 rounded-xl bg-[#FAFA96]/50 border border-[#D97706]/20 p-4">
                  <b className="text-sm text-[#0F2922]">Prioritas tindakan:</b>
                  <ol className="list-decimal pl-5 text-sm text-slate-700 mt-1 space-y-1">
                    {plagResult.action_items.map((x, i) => <li key={i}>{x}</li>)}
                  </ol>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {paper && (
        <div className="mt-6 rounded-2xl overflow-hidden border border-[#D97706]/30 bg-gradient-to-br from-[#FAFA96]/70 to-white" data-testid="recommend-panel">
          <div className="p-6 flex items-start gap-4 flex-wrap">
            <div className="w-11 h-11 rounded-xl bg-[#0F2922] text-[#FAFA96] flex items-center justify-center shrink-0"><Sparkles size={22}/></div>
            <div className="flex-1 min-w-[240px]">
              <h3 className="serif text-xl font-bold text-[#0F2922]">Rekomendasi Jurnal AI</h3>
              <p className="text-sm text-slate-600 mt-1">Claude Sonnet 5 akan membaca abstrak + kata kunci paper-mu lalu mencocokkannya dengan 3 jurnal paling tepat dari direktori ({form.target_standard}).</p>
            </div>
            <Button onClick={recommend} disabled={recommending} className="bg-[#D97706] hover:bg-[#B45309] text-white" data-testid="recommend-btn">
              {recommending ? <><Loader2 className="mr-2 animate-spin" size={14}/> Mencari…</> : <><Sparkles size={14} className="mr-2"/> Rekomendasikan 3 Jurnal</>}
            </Button>
          </div>
          {recommendations && recommendations.length > 0 && (
            <div className="grid md:grid-cols-3 gap-3 p-6 pt-0" data-testid="recommend-list">
              {recommendations.map((r, i) => (
                <div key={i} className="rounded-xl border border-slate-200 bg-white p-4 hover:shadow-md transition" data-testid={`recommend-card-${i}`}>
                  <div className="flex items-start justify-between gap-2">
                    <Badge className="bg-emerald-600 text-white">#{i+1} Match</Badge>
                    <div className="text-xs font-bold text-[#D97706]">{Math.round(r.match_score || 0)}%</div>
                  </div>
                  <h4 className="font-semibold text-[#0F2922] mt-2 serif text-base leading-snug">{r.journal.name}</h4>
                  <div className="text-[11px] text-slate-500 mt-0.5">{r.journal.publisher} · {r.journal.indexing}</div>
                  <p className="text-xs text-slate-700 mt-3 leading-relaxed italic">"{r.reason}"</p>
                  <div className="mt-3 flex items-center gap-2">
                    <a href={r.journal.url_submit} target="_blank" rel="noreferrer" className="flex-1">
                      <Button size="sm" className="w-full bg-[#0F2922] hover:bg-[#173E34] text-[#FAFA96]"><Send size={12} className="mr-1"/> Submit</Button>
                    </a>
                    <a href={r.journal.url_home} target="_blank" rel="noreferrer" className="text-slate-500 hover:text-[#0F2922] p-1.5" title="Beranda jurnal"><ExternalLink size={14}/></a>
                  </div>
                </div>
              ))}
            </div>
          )}
          {recommendations && recommendations.length === 0 && (
            <div className="px-6 pb-6 text-sm text-slate-500 italic">AI belum menemukan kecocokan sempurna. Coba ubah target standar atau lengkapi abstract paper.</div>
          )}
        </div>
      )}
    </div>
  );
}
